import { useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBolt } from "@fortawesome/free-solid-svg-icons";
import useOrderAutoAdvance from "hooks/useOrderAutoAdvance";
import "./AutoAdvanceToggle.scss";

/**
 * Per-city order auto-advance control for the orders page. When on, a backend
 * cron flips this city's stuck "not started" orders to "قيد التحضير" after the
 * configured number of minutes — for vendors who only work over WhatsApp and
 * never open the dashboard.
 *
 * Scoping: `cityId` is the orders page's resolved city — the picked city for a
 * super admin, or the admin's own city when scoped. Falsy cityId (super admin
 * with no city chosen) renders a hint, since the setting is per-city.
 *
 * The minutes input is uncontrolled and keyed by the server value, so it always
 * reflects the persisted setting without a state-syncing effect; edits are read
 * from the ref on blur/Enter.
 *
 * @param {{ cityId?: number|string, onMessage?: (type: string, head: string, content: string) => void }} props
 */

/**
 * Clamp a minutes input to [1, 120]. A blank/invalid value (parseInt → NaN)
 * falls back to `fallback` (the current server value) so clearing the box and
 * blurring is a no-op instead of silently resetting the interval.
 */
const clampMinutes = (value, fallback) => {
    const parsed = parseInt(value, 10);
    const base = Number.isNaN(parsed) ? fallback : parsed;
    return Math.max(1, Math.min(120, base));
};

const AutoAdvanceToggle = ({ cityId, onMessage }) => {
    const { data, isLoading, save, isSaving } = useOrderAutoAdvance(cityId);
    const minutesRef = useRef(null);
    // Set on the switch's mousedown (which precedes the input's blur) so the
    // blur-commit is skipped and a single toggle gesture issues exactly one
    // PATCH — the toggle's own save, which already carries the typed minutes.
    const skipCommitRef = useRef(false);
    // Bumped to force the uncontrolled input to remount back to the server value
    // (e.g. after a failed save, or to normalize "05"/blank on a no-op blur).
    const [resetNonce, setResetNonce] = useState(0);

    if (!cityId) {
        return (
            <div
                className="auto-advance auto-advance--hint"
                title="التحضير التلقائي يُضبط لكل مدينة على حدة"
            >
                <FontAwesomeIcon icon={faBolt} className="auto-advance__icon" />
                <span>اختر مدينة لضبط التحضير التلقائي</span>
            </div>
        );
    }

    const enabled = !!data?.enabled;
    const busy = isLoading || isSaving;
    const serverMinutes = data?.minutes ?? 3;

    const notifyError = (err) => {
        onMessage?.("error", "هناك خطأ", err?.message || "تعذّر تحديث التحضير التلقائي");
        setResetNonce((n) => n + 1);
    };

    const handleToggle = () => {
        skipCommitRef.current = false;
        const nextEnabled = !enabled;
        save(
            {
                enabled: nextEnabled,
                minutes: clampMinutes(minutesRef.current?.value, serverMinutes),
            },
            {
                onError: notifyError,
                onSuccess: () =>
                    onMessage?.(
                        "success",
                        "تم الحفظ",
                        nextEnabled ? "تم تفعيل التحضير التلقائي" : "تم إيقاف التحضير التلقائي",
                    ),
            },
        );
    };

    const commitMinutes = () => {
        // A switch click blurs the input first; let the toggle own the write.
        if (skipCommitRef.current) {
            skipCommitRef.current = false;
            return;
        }
        const clamped = clampMinutes(minutesRef.current?.value, serverMinutes);
        if (clamped === serverMinutes) {
            setResetNonce((n) => n + 1);
            return;
        }
        save(
            { enabled, minutes: clamped },
            {
                onError: notifyError,
                onSuccess: () =>
                    onMessage?.("success", "تم الحفظ", `مدة التحويل: ${clamped} دقيقة`),
            },
        );
    };

    return (
        <div
            className={`auto-advance ${enabled ? "auto-advance--on" : "auto-advance--off"}`}
            title="عند التفعيل: أي طلب جديد لم يبدأ يتحوّل تلقائياً إلى (قيد التحضير) بعد المدة المحددة"
        >
            <FontAwesomeIcon icon={faBolt} className="auto-advance__icon" />
            <span className="auto-advance__label">التحضير التلقائي</span>
            <button
                type="button"
                role="switch"
                aria-checked={enabled}
                aria-label="تفعيل أو إيقاف التحضير التلقائي"
                className="auto-advance__switch"
                disabled={busy}
                onMouseDown={() => {
                    skipCommitRef.current = true;
                }}
                onClick={handleToggle}
            >
                <span className="auto-advance__thumb" />
            </button>
            <span className="auto-advance__after">بعد</span>
            <input
                key={`${cityId}-${serverMinutes}-${resetNonce}`}
                ref={minutesRef}
                type="number"
                min={1}
                max={120}
                defaultValue={serverMinutes}
                disabled={!enabled || busy}
                onBlur={commitMinutes}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        e.target.blur();
                    }
                }}
                className="auto-advance__minutes"
                aria-label="عدد الدقائق قبل التحويل التلقائي"
            />
            <span className="auto-advance__unit">دقيقة</span>
        </div>
    );
};

export default AutoAdvanceToggle;
